import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Sparkles, Mail, Lock } from 'lucide-react';

const Login = () => {
  const { login } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('auth.welcomed'));
      navigate(loc.state?.from || '/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('auth.invalid'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    const redirect = encodeURIComponent(`${window.location.origin}/auth/google/callback`);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 dot-grid">
      <div className="w-full max-w-md p-8 rounded-2xl border border-white/10 bg-black/60 backdrop-blur-xl" data-testid="login-form">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-[#D4FF00] flex items-center justify-center"><Sparkles className="w-4 h-4 text-black" strokeWidth={3} /></div>
          <span className="font-heading text-xl font-black">HighlightAI</span>
        </div>
        <h1 className="font-heading text-3xl font-black mb-1">{t('auth.welcome')}</h1>
        <p className="text-zinc-500 text-sm mb-6">{t('auth.welcome.sub')}</p>

        <Button onClick={handleGoogle} variant="outline" className="w-full border-white/15 bg-white/5 hover:bg-white/10 h-11 mb-4" data-testid="login-google-btn">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/></svg>
          {t('auth.continue.google')}
        </Button>

        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-zinc-500">{t('auth.or')}</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">{t('auth.email')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input value={email} onChange={(e) => setEmail(e.target.value)} required type="email" placeholder="you@studio.com" className="pl-10 bg-white/5 border-white/10 h-11" data-testid="login-email-input" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">{t('auth.password')}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input value={password} onChange={(e) => setPassword(e.target.value)} required type="password" placeholder="••••••••" className="pl-10 bg-white/5 border-white/10 h-11" data-testid="login-password-input" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold h-11" data-testid="login-submit-btn">
            {loading ? t('auth.signing') : t('auth.signin')}
          </Button>
        </form>

        <p className="text-center text-sm text-zinc-500 mt-6">
          {t('auth.noaccount')} <Link to="/register" className="text-[#D4FF00] hover:underline" data-testid="login-link-register">{t('auth.signup.link')}</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
